/**
 * canvassOfflineQueue — IndexedDB-backed write-behind queue for knockbacks.
 *
 * Problem this solves:
 *   A canvasser standing on a rural porch in Platte County will lose LTE at
 *   least once per shift. Naively calling `POST /canvass/shifts/{id}/knocks`
 *   in that state throws a network error and the knock evaporates. This
 *   queue persists the payload to the browser so the knock survives both
 *   the network dip AND a full page reload, and replays it when the device
 *   reconnects (or when the user taps "Retry now").
 *
 * Why IndexedDB and not localStorage:
 *   - localStorage is synchronous and capped at ~5MB across the whole origin.
 *     A photo-attached knock can already blow that budget.
 *   - localStorage silently overwrites on concurrent tabs (two shifts in two
 *     tabs would corrupt each other's queue).
 *   - IndexedDB gives us an autoincrement primary key for natural ordering
 *     and transactional reads, which is what you want for "drain the queue
 *     in order, stop at the first failure, keep what's left".
 *
 * Why hand-rolled and not `idb` / `dexie`:
 *   This is the only place in the app that needs IDB. Pulling a dep for one
 *   object store bloats the bundle. The wrapper below is ~100 lines and
 *   has no external moving parts.
 *
 * Design notes:
 *   - One object store: `queued_knocks`, keyPath `id` autoincrement.
 *   - Each record mirrors what `createKnock(shiftId, body)` would post,
 *     plus metadata for the UI (queued_at, last_error).
 *   - Replay is SEQUENTIAL and STOP-ON-FIRST-FAILURE. That matters because
 *     the first failure is almost always "still offline" — blasting N
 *     parallel requests at a dead network just wastes battery, and
 *     blasting them at a live-but-slow network can overwhelm one canvasser's
 *     tenant.
 *   - Consumers can subscribe to `subscribe(fn)` to get re-rendered when
 *     the queue size changes. We use a plain EventTarget under the hood.
 *   - A synthetic-optimistic KnockRead can be built from a queued record
 *     via `synthesizeOptimisticKnock` so the UI can immediately stripe the
 *     stop as "knocked" without waiting for a server round-trip.
 *
 * Non-goals:
 *   - Conflict resolution (the backend is the source of truth for what a
 *     knock "means"; we never merge edits).
 *   - Offline reads (we only queue writes).
 *   - Cross-tab sync (two tabs of the same canvasser is not a real failure
 *     mode for door-knocking; if it happens, both tabs' queues drain
 *     independently and the backend handles dedup via route_stop_id +
 *     shift_id constraints).
 */
import type {
  KnockCreate,
  KnockRead,
  KnockResponseRead,
} from '@/types/canvass';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const DB_NAME = 'canvass-offline';
const DB_VERSION = 1;
const STORE = 'queued_knocks';

export interface QueuedKnock {
  /** Autoincremented IDB primary key. Always > 0 for persisted rows. */
  id?: number;
  shift_id: number;
  body: KnockCreate;
  queued_at: string; // ISO
  last_error?: string;
}

// ---------------------------------------------------------------------------
// Connection management
// ---------------------------------------------------------------------------

let _dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  // No IDB in SSR / tests without jsdom — fall back to a rejected promise
  // so callers can decide whether to swallow or surface. In practice our
  // only callers are React components which only run in the browser.
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not available'));
  }
  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IDB open failed'));
  });
  return _dbPromise;
}

function txStore(
  db: IDBDatabase,
  mode: IDBTransactionMode,
): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

// Wrap an IDBRequest in a Promise so callers can await it cleanly.
function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error || new Error('IDB request failed'));
  });
}

// ---------------------------------------------------------------------------
// Queue API
// ---------------------------------------------------------------------------

/**
 * Persist a knock payload so it can be replayed later. Returns the queued
 * row WITH its autoincremented id filled in, so callers can synthesize an
 * optimistic KnockRead from it.
 */
export async function enqueueKnock(
  shiftId: number,
  body: KnockCreate,
): Promise<QueuedKnock> {
  const db = await openDB();
  const row: QueuedKnock = {
    shift_id: shiftId,
    body,
    queued_at: new Date().toISOString(),
  };
  const store = txStore(db, 'readwrite');
  const id = await req(store.add(row));
  const saved: QueuedKnock = { ...row, id: Number(id) };
  emitChange();
  return saved;
}

/** All queued knocks across all shifts, oldest first. */
export async function listQueuedKnocks(): Promise<QueuedKnock[]> {
  const db = await openDB();
  const store = txStore(db, 'readonly');
  const rows = (await req(store.getAll())) as QueuedKnock[];
  // IDB getAll returns insertion order for autoincrement keys, but sort
  // defensively — queued_at is the real source of truth if the caller
  // wants chronological drain order.
  return rows.sort((a, b) => (a.queued_at < b.queued_at ? -1 : 1));
}

/** Subset filtered to one shift, for the per-shift pending badge. */
export async function listQueuedKnocksForShift(
  shiftId: number,
): Promise<QueuedKnock[]> {
  const all = await listQueuedKnocks();
  return all.filter((q) => q.shift_id === shiftId);
}

/** Remove a queued row by id, typically after a successful replay. */
export async function removeQueuedKnock(queueId: number): Promise<void> {
  const db = await openDB();
  const store = txStore(db, 'readwrite');
  await req(store.delete(queueId));
  emitChange();
}

/** Update the last_error field on a queued row after a failed replay. */
export async function markQueuedKnockError(
  queueId: number,
  message: string,
): Promise<void> {
  const db = await openDB();
  const store = txStore(db, 'readwrite');
  const row = (await req(store.get(queueId))) as QueuedKnock | undefined;
  if (!row) return;
  row.last_error = message;
  await req(store.put(row));
  emitChange();
}

/** Drop everything — used by the "clear queue" escape hatch. */
export async function clearQueue(): Promise<void> {
  const db = await openDB();
  const store = txStore(db, 'readwrite');
  await req(store.clear());
  emitChange();
}

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------

export interface ReplayResult {
  attempted: number;
  succeeded: number;
  failed: number;
  stoppedEarly: boolean;
  lastError?: string;
}

/**
 * Attempt to drain every queued knock, in chronological order. Stops at the
 * first failure (because the most common failure is "still offline" and
 * hammering a dead network is wasteful).
 *
 * The caller passes in the live `createKnock` function rather than us
 * importing it directly to keep this module free of an `@/api/*` circular
 * dependency (canvass.ts already imports from here).
 */
export async function replayAllKnocks(
  sendFn: (shiftId: number, body: KnockCreate) => Promise<KnockRead>,
): Promise<ReplayResult> {
  const queue = await listQueuedKnocks();
  const result: ReplayResult = {
    attempted: 0,
    succeeded: 0,
    failed: 0,
    stoppedEarly: false,
  };
  for (const q of queue) {
    result.attempted += 1;
    try {
      await sendFn(q.shift_id, q.body);
      if (q.id !== undefined) await removeQueuedKnock(q.id);
      result.succeeded += 1;
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any;
      const status = err?.response?.status as number | undefined;
      const detail = err?.response?.data?.detail as string | undefined;
      const message =
        detail || (status ? `Backend error ${status}` : 'Network unreachable');
      result.failed += 1;
      result.lastError = message;
      if (q.id !== undefined) {
        await markQueuedKnockError(q.id, message);
      }
      // If it's a genuine HTTP error (server was reachable, said no), we
      // still stop the drain so the user sees the problem instead of
      // burying it under a stack of similar errors. If it's a network
      // error (!status), stopping is obviously the right call.
      result.stoppedEarly = true;
      break;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Optimistic rendering helper
// ---------------------------------------------------------------------------

/**
 * Fabricate a KnockRead shape from a queued row so the UI can immediately
 * stripe the stop as "knocked" without waiting for a round-trip. The id is
 * negative (`-queueId`) so downstream code can detect "this is optimistic,
 * not persisted" if it needs to (e.g. disable an edit button on pending
 * responses).
 */
export function synthesizeOptimisticKnock(q: QueuedKnock): KnockRead {
  const syntheticResponse: KnockResponseRead | null = q.body.response
    ? {
        id: -1,
        knockback_id: -1,
        answered_by: q.body.response.answered_by ?? null,
        top_issues: q.body.response.top_issues ?? null,
        candidate_lean: q.body.response.candidate_lean ?? null,
        vote_intent: q.body.response.vote_intent ?? null,
        yard_sign_interest: q.body.response.yard_sign_interest ?? null,
        volunteer_interest: q.body.response.volunteer_interest ?? null,
        donate_interest: q.body.response.donate_interest ?? null,
        escalate_flag: q.body.response.escalate_flag ?? false,
        escalate_reason: q.body.response.escalate_reason ?? null,
        photo_path: q.body.response.photo_path ?? null,
        created_at: q.queued_at,
        updated_at: null,
      }
    : null;
  return {
    id: q.id !== undefined ? -q.id : -Date.now(),
    route_stop_id: q.body.route_stop_id,
    canvasser_id: 0,
    canvass_shift_id: q.shift_id,
    outcome: q.body.outcome,
    notes: q.body.notes ?? null,
    gps_lat: q.body.gps_lat ?? null,
    gps_lng: q.body.gps_lng ?? null,
    timestamp: q.queued_at,
    response: syntheticResponse,
  };
}

export function isOptimisticKnock(knock: KnockRead): boolean {
  return knock.id < 0;
}

// ---------------------------------------------------------------------------
// Change notifications
// ---------------------------------------------------------------------------

// Plain EventTarget so components can re-render when the queue changes
// without having to poll. We could use BroadcastChannel for cross-tab
// sync, but as noted in the module docstring, cross-tab is not a real
// failure mode here.
const bus =
  typeof window !== 'undefined' && typeof EventTarget !== 'undefined'
    ? new EventTarget()
    : null;

function emitChange() {
  if (bus) bus.dispatchEvent(new Event('change'));
}

/**
 * Subscribe to queue-change events. Returns an unsubscribe function.
 * Safe to call from useEffect.
 */
export function subscribe(fn: () => void): () => void {
  if (!bus) return () => {};
  const handler = () => fn();
  bus.addEventListener('change', handler);
  return () => bus.removeEventListener('change', handler);
}
